import { CooldownButton } from "@components/cooldownButton/cooldownButton";
import { useAuthActions } from "@context/authContext/actions";
import { client } from "@graphql2/client";
import { getSession } from "@graphql2/graphql_op";
import { GetSessionQuery } from "@graphql2/types";
import { LoginProps } from "@pages/login/loginContainer";
import { LoginBoxStyle, LoginStyle } from "@pages/login/loginStyle";
import { parseMFATokenError } from "@pages/login/parseMFATokenError";
import { parseError } from "@utils/parseError";
import { Button, Form, Input, message, Row } from "antd";
import { useForm } from "antd/lib/form/Form";
import * as React from "react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

const fetchSession = async () => {
    const result = await client.query<GetSessionQuery>({
        query: getSession
    });

    if (result.data) {
        return result.data.session;
    }
};

export interface FormValues {
    email: string;
    password: string;
}

export const Login: React.FC<LoginProps> = (props: LoginProps) => {
    const [loginProgress, setLoginProgress] = useState(false);
    const [confirmCodeChallenge, setConfirmCodeChallenge] = useState<string | null>(null);
    const [loginForm] = useForm();
    const [confirmCodeForm] = useForm();
    const intl = useIntl();
    const location = useLocation();
    const { loginAction, updateSessionInfo } = useAuthActions();
    const { login, regenerateConfirmCode, consumeConfirmCode, history } = props;

    const submitForm = async (values) => {
        try {
            setLoginProgress(true);

            const { email, password } = values;
            const response = await login({ variables: { email, password } });

            const loginData = response?.data?.login;

            if (!loginData) {
                setLoginProgress(false);
                return;
            }

            if (loginData.confirmCodeChallengeId) {
                setConfirmCodeChallenge(loginData.confirmCodeChallengeId);
                setLoginProgress(false);

                return;
            }

            loginAction(loginData.jwtToken!);
            const sessionData = await fetchSession();
            if (sessionData) {
                updateSessionInfo(sessionData);
            }
            const queryParams = new URLSearchParams(location.search);
            const redirectTarget = queryParams.get("redirect");
            history.push(redirectTarget || "/");
        } catch (errors) {
            const msg = parseError(errors, intl);

            setLoginProgress(false);

            if (msg === "error.rateLimited") {
                message.error(intl.formatMessage({ id: "auth.MFA.error.rateLimit" }));
                return;
            }

            if (msg === "error.notAllowed") {
                message.error(intl.formatMessage({ id: "login.failed" }));
                return;
            }

            message.error(intl.formatMessage({ id: msg }));
        }
    };

    const submitConfirmToken = async (values) => {
        setLoginProgress(true);

        const { confirmCode } = values;
        if (!confirmCode) {
            setLoginProgress(false);
            return;
        }

        if (!confirmCodeChallenge) {
            message.error(intl.formatMessage({ id: "login.failed" }));
            setConfirmCodeChallenge(null);
            setLoginProgress(false);
            return;
        }

        try {
            const response = await consumeConfirmCode({
                variables: {
                    confirmCodeChallengeId: confirmCodeChallenge,
                    confirmCode
                }
            });

            const confirmCodeData = response.data?.consumeConfirmCode;

            if (!confirmCodeData) {
                setLoginProgress(false);
                return;
            }

            loginAction(confirmCodeData.jwtToken!);
            const sessionData = await fetchSession();
            if (sessionData) {
                updateSessionInfo(sessionData);
            }
            const queryParams = new URLSearchParams(location.search);
            const redirectTarget = queryParams.get("redirect");
            history.push(redirectTarget || "/");
        } catch (errors) {
            const msg = parseMFATokenError(errors);
            if (msg === "auth.MFA.error.loginChallengeNotFound") {
                setConfirmCodeChallenge(null);
                setLoginProgress(false);
            }
            message.error(intl.formatMessage({ id: msg }));

            setLoginProgress(false);
        }
    };

    const regenerateConfirmToken = async () => {
        if (!confirmCodeChallenge) {
            message.error(intl.formatMessage({ id: "login.failed" }));
            setConfirmCodeChallenge(null);
            return;
        }

        try {
            const response = await regenerateConfirmCode({ variables: { confirmCodeChallengeId: confirmCodeChallenge } });

            const regenerateConfirmCodeData = response.data?.regenerateConfirmCode;

            if (!regenerateConfirmCodeData) {
                return;
            }
        } catch (errors) {
            const msg = parseMFATokenError(errors);

            if (msg === "auth.MFA.error.loginChallengeNotFound") {
                setConfirmCodeChallenge(null);
                setLoginProgress(false);
            }

            message.error(intl.formatMessage({ id: msg }));

            setLoginProgress(false);
        }
    };

    return (
        <LoginStyle>
            <LoginBoxStyle>
                <img
                    alt="Logo Energylab"
                    src="https://res.cloudinary.com/energylab/image/upload/v1593509749/development/logo_login.png"
                />
                <div className="login__cover">
                    {!confirmCodeChallenge && (
                        <Form
                            hideRequiredMark
                            className="login__form"
                            form={loginForm}
                            layout="vertical"
                            onFinish={submitForm}
                        >
                            <Form.Item
                                colon={false}
                                label={`${intl.formatMessage({ id: "form.email" })}*`}
                                name="email"
                                rules={
                                    [
                                        { required: true, message: intl.formatMessage({ id: "form.isrequired" }) },
                                        { type: "email", message: intl.formatMessage({ id: "form.invalidemail" }) }
                                    ]
                                }
                                validateTrigger="onSubmit"
                            >
                                <Input placeholder={intl.formatMessage({ id: "form.email" })} />
                            </Form.Item>
                            <Form.Item
                                colon={false}
                                label={`${intl.formatMessage({ id: "form.password" })}*`}
                                name="password"
                                rules={[
                                    { required: true, message: intl.formatMessage({ id: "form.isrequired" }) }
                                ]}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: "form.password" })}
                                    type="password"
                                />
                            </Form.Item>
                            <Button
                                htmlType="submit"
                                loading={loginProgress}
                            >
                                <FormattedMessage id="form.login" />
                            </Button>
                        </Form>
                    )}
                    {/* eslint-disable-next-line no-implicit-coercion */}
                    {!!confirmCodeChallenge && (
                        <Form
                            hideRequiredMark
                            className="login__form"
                            form={confirmCodeForm}
                            layout="vertical"
                            onFinish={submitConfirmToken}
                        >
                            <Form.Item
                                colon={false}
                                label={`${intl.formatMessage({ id: "form.confirmCode" })}*`}
                                name="confirmCode"
                                rules={
                                    [
                                        { required: true, message: intl.formatMessage({ id: "form.isrequired" }) }
                                    ]
                                }
                                validateTrigger="onSubmit"
                            >
                                <Input placeholder={intl.formatMessage({ id: "form.confirmCode" })} />
                            </Form.Item>
                            <Row justify="space-between">
                                <CooldownButton
                                    cooldown={60}
                                    onClick={regenerateConfirmToken}
                                >
                                    <FormattedMessage id="auth.MFA.resendConfirmCode" />
                                </CooldownButton>
                                <Button
                                    htmlType="submit"
                                    loading={loginProgress}
                                    type="primary"
                                >
                                    <FormattedMessage id="auth.MFA.sendConfirmCode" />
                                </Button>
                            </Row>
                        </Form>
                    )}
                </div>
            </LoginBoxStyle>
        </LoginStyle>
    );
};
